import { observer } from 'mobx-react-lite';

import { YStack, XStack } from '@/components/ui/layout/layout';

import { CollapsingRow } from './collapsing-row';
import { pendingEntries } from './connection-actions';
import { usePendingConnectionsQuery } from './connections.query';
import { RequestColumn } from './request-column';
import { useRequestLines } from './use-request-lines';

/** The top of the Wiki page: one card with two columns, the requests that came in and wait for the user's answer, and
 * the requests and invites that the user sent. A column with no lines is left out, and the card with it when both are
 * empty. The card closes once no line is open and none is closing. */
export const ConnectionRequests = observer(() => {
  const { data: people = [] } = usePendingConnectionsQuery();
  const lines = useRequestLines(
    pendingEntries(people, ['accept', 'requested', 'invited', 'inviteAgain'])
  );
  if (lines.entries.length === 0) return null;

  const openCount = (entries: typeof lines.entries) =>
    entries.filter(({ accountId }) => lines.statusOf(accountId) !== 'closed')
      .length;
  const received = lines.entries.filter(
    ({ entry }) => entry.state === 'accept'
  );
  const sent = lines.entries.filter(({ entry }) => entry.state !== 'accept');
  // The card closes after its last line, and its headers keep their last count while it fades
  const isClosing = openCount(lines.entries) === 0;
  const columns = [
    { title: 'Received', entries: received, count: openCount(received) },
    { title: 'Sent', entries: sent, count: openCount(sent) },
  ].filter(
    ({ entries, count }) => count > 0 || (isClosing && entries.length > 0)
  );

  return (
    // The page spaces its parts with a gap, which closes with the card: the card carries the gap as padding
    <CollapsingRow
      collapsed={isClosing}
      className="-mb-4 flex-none"
      onCollapsed={lines.reset}
    >
      <YStack className="pb-4">
        <YStack
          data-request-card
          tabIndex={-1}
          className="rounded-xl border border-border outline-none [container-type:inline-size]"
        >
          {/* Below this width the columns stack, with the requests that came in on top */}
          <XStack className="[@container(max-width:719px)]:flex-col">
            {columns.map((column, index) => (
              <RequestColumn
                key={column.title}
                {...column}
                lines={lines}
                hasDivider={index > 0}
              />
            ))}
          </XStack>
        </YStack>
      </YStack>
    </CollapsingRow>
  );
});
